import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, withStyles } from '@material-ui/core'
import styles from './styles'
import { CoreButton } from 'src/components/core'

// Ideas data - extracted for better organization
const IDEAS_DATA = {
  yoga: {
    title: 'Guided Yoga Flow',
    icon: '🧘',
    text:
      'Design an interactive guided yoga sequence for beginners focusing on stress relief. Each pose should be shown with a smooth fade-in animation, with breathing cues below (inhale, hold, exhale) and a soft sound chime between transitions. Include calming background music, a pastel gradient background, and a tracker that gently fills as the session progresses. At the end, show a reflective quote and a "Mark as Complete" button.',
  },
  programming: {
    title: 'Programming Challenge Generator',
    icon: '💡',
    text:
      'Create an interactive daily coding challenge for new JavaScript learners. Start with a warm-up puzzle, followed by a real-world problem like form validation or localStorage use. Include hints that unlock after 30 seconds, and allow users to test their code live. At the end, show a progress ring and award a "Problem Solver" badge. Use a neon code editor theme with a typewriter animation for loading.',
  },
  geography: {
    title: 'Geography Explorer',
    icon: '🌍',
    text:
      'Build a drag-and-drop interactive game where kids match countries with their capitals. Add subtle animations when answers are correct and a bounce effect when they\'re not. Include a "Did you know?" popup with a fun fact after each correct match. Track score with a globe that fills in as users complete matches. Use bright, playful colors and friendly fonts.',
  },
  math: {
    title: 'Math Puzzle Tiles',
    icon: '🧮',
    text:
      'Design a math logic puzzle block for elementary school students that uses drag-and-drop tiles to complete equations. Each equation should respond with sound feedback and color highlights. Include a progress bar and a "Try Again" button when mistakes are made. Use a chalkboard-style theme with animated chalk writing effects.',
  },
  study: {
    title: 'Study Streak Tracker',
    icon: '🎓',
    text:
      'Create an interactive study timer that tracks streaks for each day a user studies more than 25 minutes. Include a cute animated owl that reacts differently based on study performance (sleepy, cheering, reading). Add motivational quotes after each session and a button to "Review My Notes." Visualize the streak with stars or calendar checkmarks. Use a warm, focused color scheme with golds and deep blues.',
  },
  language: {
    title: 'Language Practice Chat',
    icon: '🗣️',
    text:
      'Build an interactive language practice tool where learners can role-play a conversation in a café. Let them choose a language (like Spanish or French), and present a dialogue with multiple response options. Provide instant feedback on grammar and tone. Use speech bubbles, a typing animation, and soft background café sounds. At the end, give a fluency rating and suggest phrases to improve.',
  },
}

// Star Icon Component - extracted for better readability
const StarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="33"
    height="32"
    viewBox="0 0 33 32"
    fill="none"
  >
    <path
      d="M12.053 29.332C12.6327 29.332 13.1077 28.9056 13.1882 28.3023C13.9853 22.9288 14.718 22.1806 19.8871 21.5934C20.4829 21.521 20.9418 21.0223 20.9418 20.4431C20.9418 19.8559 20.491 19.3732 19.8951 19.2847C14.7583 18.5688 14.1222 17.9413 13.1882 12.5758C13.0835 11.9805 12.6246 11.5542 12.053 11.5542C11.4652 11.5542 10.9982 11.9805 10.9016 12.5839C10.1206 17.9494 9.3879 18.6975 4.22686 19.2847C3.61495 19.3652 3.16406 19.8478 3.16406 20.4431C3.16406 21.0223 3.59885 21.5049 4.21076 21.5934C9.35569 22.3254 9.98371 22.9448 10.9016 28.3104C11.0224 28.9137 11.4893 29.332 12.053 29.332Z"
      fill="url(#paint0_linear_25186_53856)"
    />
    <path
      d="M24.2743 13.7772C24.6366 13.7772 24.9335 13.5108 24.9838 13.1337C25.482 9.77524 25.94 9.30767 29.1706 8.94065C29.543 8.8954 29.8299 8.58368 29.8299 8.22169C29.8299 7.85467 29.5481 7.55302 29.1757 7.49771C25.9651 7.05025 25.5676 6.65809 24.9838 3.30465C24.9184 2.9326 24.6316 2.66614 24.2743 2.66614C23.907 2.66614 23.6151 2.9326 23.5547 3.30968C23.0666 6.66312 22.6086 7.13069 19.383 7.49771C19.0006 7.54799 18.7188 7.84965 18.7188 8.22169C18.7188 8.58368 18.9905 8.88534 19.3729 8.94065C22.5885 9.39816 22.981 9.78529 23.5547 13.1387C23.6302 13.5158 23.9221 13.7772 24.2743 13.7772Z"
      fill="url(#paint1_linear_25186_53856)"
    />
    <path
      d="M27.059 29.3338C27.2402 29.3338 27.3886 29.2005 27.4138 29.012C27.6629 27.3327 27.8919 27.099 29.5072 26.9155C29.6934 26.8928 29.8368 26.737 29.8368 26.556C29.8368 26.3725 29.6959 26.2216 29.5097 26.194C27.9044 25.9703 27.7057 25.7742 27.4138 24.0975C27.3811 23.9114 27.2377 23.7782 27.059 23.7782C26.8754 23.7782 26.7294 23.9114 26.6992 24.1C26.4552 25.7767 26.2262 26.0105 24.6134 26.194C24.4222 26.2191 24.2812 26.37 24.2812 26.556C24.2812 26.737 24.4171 26.8878 24.6083 26.9155C26.2161 27.1442 26.4124 27.3378 26.6992 29.0145C26.737 29.203 26.8829 29.3338 27.059 29.3338Z"
      fill="url(#paint2_linear_25186_53856)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_25186_53856"
        x1="3.16406"
        y1="20.4431"
        x2="17.101"
        y2="20.4431"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFCC33" />
        <stop offset="1" stopColor="#FF9900" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_25186_53856"
        x1="18.7188"
        y1="8.22169"
        x2="27.4293"
        y2="8.22169"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFCC33" />
        <stop offset="1" stopColor="#FF9900" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_25186_53856"
        x1="24.2813"
        y1="26.556"
        x2="28.6365"
        y2="26.556"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFCC33" />
        <stop offset="1" stopColor="#FF9900" />
      </linearGradient>
    </defs>
  </svg>
)

const Header = ({ classes }) => (
  <div className={classes.headerContainer}>
    <span className={classes.headerIcon}>
      <StarIcon />
    </span>
    <Typography className={classes.header}>
      Turn ideas into Interactions.
    </Typography>
  </div>
)

const TextArea = ({ textContent, onTextChange, classes }) => (
  <div className={classes.textareaContainer}>
    <textarea
      value={textContent}
      onChange={onTextChange}
      placeholder="Describe what you want to build…"
      className={classes.textarea}
    />
  </div>
)

const IdeaButton = ({ idea, ideaKey, isSelected, onSelect, classes }) => (
  <button
    onClick={() => onSelect(ideaKey)}
    className={`${classes.ideaButton} ${
      isSelected ? classes.ideaButtonSelected : ''
    }`}
  >
    <span className={classes.buttonIcon}>{idea.icon}</span>
    {idea.title}
  </button>
)

const IdeaButtonsRow = ({
  ideas,
  selectedIdea,
  onIdeaSelect,
  classes,
  isLastRow = false,
}) => {
  const rowClass = isLastRow ? classes.buttonRowLast : classes.buttonRow

  return (
    <div className={rowClass}>
      {ideas.map(([key, idea]) => (
        <IdeaButton
          key={key}
          ideaKey={key}
          idea={idea}
          isSelected={selectedIdea === key}
          onSelect={onIdeaSelect}
          classes={classes}
        />
      ))}
    </div>
  )
}

const AiGuide = ({ classes, onProceed }) => {
  const [selectedIdea, setSelectedIdea] = useState('')
  const [textContent, setTextContent] = useState('')

  const handleIdeaSelect = ideaKey => {
    setSelectedIdea(ideaKey)
    setTextContent(IDEAS_DATA[ideaKey].text)
  }

  const handleTextChange = e => {
    setTextContent(e.target.value)
  }

  const handleProceed = () => {
    if (onProceed && textContent.trim()) {
      onProceed(textContent.trim())
    }
  }

  const ideasEntries = Object.entries(IDEAS_DATA)
  const firstRowIdeas = ideasEntries.slice(0, 3)
  const secondRowIdeas = ideasEntries.slice(3, 6)

  return (
    <div className={classes.content}>
      <div className={classes.container}>
        <Header classes={classes} />

        <TextArea
          textContent={textContent}
          onTextChange={handleTextChange}
          classes={classes}
        />

        <IdeaButtonsRow
          ideas={firstRowIdeas}
          selectedIdea={selectedIdea}
          onIdeaSelect={handleIdeaSelect}
          classes={classes}
        />

        <IdeaButtonsRow
          ideas={secondRowIdeas}
          selectedIdea={selectedIdea}
          onIdeaSelect={handleIdeaSelect}
          classes={classes}
          isLastRow
        />

        <div className={classes.proceedButtonContainer}>
          <CoreButton
            fullWidth
            variant="contained"
            onClick={handleProceed}
            disabled={!textContent.trim()}
          >
            {'Proceed'}
          </CoreButton>
        </div>
      </div>
    </div>
  )
}

AiGuide.propTypes = {
  classes: PropTypes.object.isRequired,
  onProceed: PropTypes.func,
}

export default withStyles(styles)(AiGuide)
